/** @type {import('../help-index.js').HelpScreen} */
export const BOD_VAS_HELP = {
  screenId: 'bod-vas',
  label: 'Vasijas',
  navPath: 'Bodega → Vasijas',
  keywords: ['bodega', 'vasija', 'tanque', 'barrica', 'lote de barricas', 'movimiento', 'trasiego', 'merma', 'trazabilidad', 'inv', 'seguimiento', 'analisis'].map((k) => k.toLowerCase()),
  status: 'draft',
  summary: 'Acá ves las vasijas de la bodega, el vino que tienen adentro y los movimientos entre ellas.',
  sidebarItems: [
    {
      id: 'bod-vas-list',
      label: 'Vasijas',
      description: 'Listado con filtros y ficha de cada vasija.',
      detail: {
        sections: [
          {
            title: 'Paso 1 — Filtrar la lista',
            bullets: [
              'Arriba podés filtrar por tipo de vasija, vino, añada y estado (activa o de baja).',
              '«Solo con vino» oculta las vacías.',
              '«Ver barricas» muestra los lotes de barricas como una sola fila (la suma de litros) y las barricas que no están en un lote. Las que ya pertenecen a un lote no se listan una por una.',
              '«Stock a fecha» muestra litros, kilos y lote al cierre de ese día. Vacío es el estado de hoy. La ficha de la vasija sigue siendo el estado actual.',
              'La lista muestra número INV, capacidad, litros, porcentaje de llenado, lote, vino, añada y estado del vino. Si el lote mezcla añadas, se ven todas con su porcentaje.',
              'Abajo están los litros que hay en las vasijas y la capacidad total, en litros. Si hay filtros, esas sumas son solo de lo que se está viendo. Si no, son de todas.',
            ],
          },
          {
            title: 'Paso 2 — Alta o edición',
            fields: [
              { label: 'Número INV', description: 'Número de la vasija para el Instituto Nacional de Vitivinicultura. Es obligatorio en tanques y piletas. En una barrica es opcional: si no lo tenés, alcanza con un nombre. Puede cambiar; el sistema guarda además un identificador interno.' },
              { label: 'Tipo', description: 'Tanque, pileta, barrica, etc. Se cargan en Maestros.' },
              { label: 'Capacidad y unidad', description: 'En litros o hectolitros. Por dentro el sistema trabaja siempre en litros.' },
              { label: 'Ubicación', description: 'Dónde está en la bodega.' },
              { label: 'Datos de barrica', description: 'Si el tipo es barrica, aparecen tonelería, madera, tostado y año de compra. Los usos se cuentan solos cada vez que se llena vacía.' },
              { label: 'Estado', description: '«De baja» retira la vasija. Solo se puede si está vacía.' },
            ],
          },
          {
            title: 'Paso 3 — Ficha',
            intro: 'Tocá una fila (no el lápiz) para abrir la ficha.',
            bullets: [
              'Datos: capacidad, ubicación y, en barricas, los usos.',
              'Vino: lote y composición (vino, añada y porcentaje). «Trazabilidad completa» repite esas líneas: el + muestra de qué vasija vinieron esos litros y cuántos de cada una. Se puede seguir abriendo hasta la cosecha. Si a esa pileta entraron varios CIU, aparecen todos, con su fecha, kilos, finca y cuadro.',
              'Análisis: arriba, los datos de esta vasija. Una muestra cargada acá no aparece en otra vasija del mismo lote. Si el vino llegó de otra vasija, hereda esos datos hasta la fecha del movimiento y se marca como heredado. Una muestra de esta vasija reemplaza ese dato. En un corte, si todos los vinos tienen el dato, se muestra el promedio ponderado por litros. Abajo están solo las muestras tomadas en esta vasija.',
              'Adiciones: lo agregado a este vino en esta vasija. Muestra la dosis, el total y los litros (o los kilos, si estaba en encube) que había en ese momento. Si el vino llega de otra vasija, esas adiciones vienen con él y figuran como heredadas. Una adición hecha después en la vasija de origen no se copia.',
              'Movimientos: los últimos trasiegos, mermas e ingresos de esa vasija.',
              'Si la vasija está en encube, desde la ficha se registra el descube: kilos de orujo, litros y el vino de cada vasija. El tinto, blanco o rosado es el color de ese vino. Si mandás a dos vasijas, cada una puede llevar un vino distinto (por ejemplo escurrido y prensado).',
              'Si ya hay vino líquido, el desborre pide los litros de borra y si se descartan o van a otra vasija.',
            ],
          },
        ],
      },
    },
    {
      id: 'bod-vas-mov',
      label: 'Movimientos',
      description: 'Trasiegos, mermas y ajustes entre vasijas o lotes de barricas.',
      detail: {
        sections: [
          {
            title: 'Paso 1 — Elegir el tipo',
            bullets: [
              'Trasiego: el vino sale de un lugar y entra en otro. El lugar puede ser una vasija o un lote de barricas. Si entra menos de lo que sale, la diferencia queda como merma.',
              'Merma: solo sale vino. En un lote se reparte entre las barricas según el vino que tiene cada una.',
              'Ajuste: corrige litros. Para meter vino en un lugar vacío hay que indicar el lote de vino.',
            ],
          },
          {
            title: 'Paso 2 — Elegir origen y destino',
            bullets: [
              'Escribí el número, el nombre o el lote. Aparecen piletas, tanques, barricas y lotes de barricas.',
              'Hacia un lote, el vino llena las barricas en orden de número, hasta la capacidad. Sirven las vacías y las que ya tienen ese mismo vino.',
              'Desde un lote, se vacían las barricas en ese mismo orden hasta completar los litros. El lote tiene que tener un solo vino.',
              'Si alguna barrica del lote tiene otro vino, elegí esa barrica. El lote no arma el corte solo.',
              'Antes de registrar, el sistema dice cuántas barricas se llenan o se vacían.',
              'Un trasiego también puede tener varias líneas.',
            ],
          },
          {
            title: 'Paso 3 — Corte y lista',
            intro: 'Si el destino ya tiene otro vino, el sistema avisa y crea un lote de corte. Podés ponerle código y vino. Si las categorías no coinciden, elegís el grupo del corte.',
            bullets: [
              'La lista se puede filtrar por número, por fechas y por origen o destino. Tocá la fila para ver cada barrica.',
              'Anular solo funciona si ese movimiento es el último de cada vasija que tocó.',
            ],
          },
        ],
      },
    },
    {
      id: 'bod-vas-bar',
      label: 'Barricas',
      description: 'Barricas sueltas y lotes de barricas.',
      detail: {
        sections: [
          {
            title: 'Paso 1 — Lote o barrica suelta',
            bullets: [
              'Nuevo lote crea varias barricas iguales y las agrupa. Por ejemplo, 10 barricas de 225 litros. En Vasijas, con «Ver barricas», ese lote aparece como 2250 litros y no como diez filas.',
              'Si la cantidad es 0, el lote queda vacío y después le asignás barricas que ya existen.',
              'Nueva barrica carga una sola. El número de INV es opcional. Si no lo ponés, el nombre es obligatorio.',
              'Una barrica sin lote se ve en Vasijas cuando marcás «Ver barricas».',
            ],
          },
          {
            title: 'Paso 2 — Armar el lote',
            bullets: [
              'Tocá un lote para ver sus barricas, quitar una, asignar barricas sueltas o crear más con la misma capacidad.',
              'Anular el lote no borra las barricas: quedan sueltas.',
              'El vino sigue estando en cada barrica. Un movimiento del lote se reparte en esas barricas.',
            ],
          },
        ],
      },
    },
    {
      id: 'bod-vas-seg',
      label: 'Seguimiento',
      description: 'Datos analíticos de las vasijas y la evolución de una.',
      detail: {
        sections: [
          {
            title: 'Todas las vasijas',
            bullets: [
              'Elegí uno o más datos (por ejemplo sulfuroso libre y acidez volátil) y tocá Ver.',
              'SO2 molecular es un dato calculado, no una muestra. Usa el sulfuroso libre y el pH de la vasija, a 20 °C: libre dividido (1 + 10 elevado a pH − 1,81). El alcohol no entra. Si a la vasija le falta el pH o el libre, no se muestra.',
              'Aparecen solo las vasijas que tienen vino y al menos uno de esos datos.',
              'Al lado del valor está la fecha si la muestra es de esa vasija. Heredado es un dato que llegó con el vino desde otra vasija. Promedio ponderado es un corte en el que todos los vinos tienen ese dato. Si a alguno le falta, ese dato no se muestra.',
            ],
          },
          {
            title: 'Evolución de una vasija',
            bullets: [
              'Elegí la vasija y los datos. El gráfico es del vino que está hoy ahí, no de vinos que ya salieron. Si elegís SO2 molecular, cada punto se calcula con el pH y el sulfuroso libre de esa fecha.',
              'Cada punto es una fecha en la que cambia el dato. El punto lleno es una muestra de esta vasija. El punto hueco es un dato heredado de otra vasija, o un promedio ponderado del corte, usando análisis de hasta la fecha del movimiento.',
              'Debajo del gráfico está el mismo listado, con la fecha de cada dato.',
            ],
          },
        ],
      },
    },
  ],
}

/** @type {import('../help-index.js').HelpScreen} */
export const BOD_COS_HELP = {
  screenId: 'bod-cos',
  label: 'Cosecha',
  navPath: 'Bodega → Cosecha',
  keywords: ['cosecha', 'uva', 'añada', 'ingreso', 'mosto', 'ciu', 'variedad', 'finca', 'cuadro', 'comparativo'].map((k) => k.toLowerCase()),
  status: 'draft',
  summary: 'El ingreso de uva crea el lote de origen. Toda la trazabilidad parte de ahí.',
  sidebarItems: [
    {
      id: 'bod-cos-new',
      label: 'Ingreso de uva',
      description: 'Carga la cosecha en una vasija vacía.',
      detail: {
        sections: [
          {
            title: 'Antes de empezar',
            bullets: [
              'La añada, las variedades, las fincas y los cuadros se dan de alta en Maestros.',
              'La vasija destino tiene que estar vacía, o ya en encube con la misma uva y añada.',
              'Se puede corregir el número de CIU, la fecha y la variedad. La fecha no puede cruzarse con otro movimiento de esa vasija. Eliminar solo es posible si ese ingreso sigue siendo el último movimiento de la vasija.',
            ],
          },
          {
            title: 'Qué completar',
            fields: [
              { label: 'Fecha', description: 'Día del ingreso.' },
              { label: 'Variedad y tipo de vino', description: 'La variedad es la uva (Malbec, Chardonnay). Si entra en kilos, el tipo de vino (tinto, blanco o rosado) se elige en el descube. Si entra ya en litros, se elige acá.' },
              { label: 'Kilos de uva, finca y cuadro', description: 'En tintos la uva entra entera a la pileta. Dejá los litros vacíos: la vasija queda en encube y todavía no hay vino líquido. La finca puede ser propia o de terceros, y el cuadro es el lote del viñedo.' },
              { label: 'Número de CIU', description: 'Comprobante de ingreso de uva del INV.' },
              { label: 'Litros obtenidos', description: 'Solo si ya hay líquido, como en un blanco prensado. El lote nace en estado Mosto.' },
              { label: 'Código de lote', description: 'Opcional. Si lo dejás vacío, el sistema arma uno (L-año-número).' },
            ],
          },
        ],
      },
    },
    {
      id: 'bod-cos-resumen',
      label: 'Resumen',
      description: 'Totales de una cosecha.',
      detail: {
        sections: [
          {
            title: 'Cómo se lee',
            bullets: [
              'Elegí la cosecha (la añada).',
              'Los totales se pueden ver por variedad, por tipo de uva (tinta, blanca, rosada) o por tipo de vino (tinto, blanco, rosado).',
              'Abajo queda el detalle de cada ingreso, con el número de CIU.',
            ],
          },
        ],
      },
    },
    {
      id: 'bod-cos-comparativo',
      label: 'Comparativo',
      description: 'Kilos y litros entre cosechas.',
      detail: {
        sections: [
          {
            title: 'Cómo se lee',
            bullets: [
              'Marcá las cosechas que querés comparar.',
              'Filtrá por color de la uva (tinta, blanca, rosada) o por una variedad.',
              'Los kilos son la uva ingresada. Los litros son los que ya se cargaron; la uva en encube todavía no suma litros.',
            ],
          },
        ],
      },
    },
  ],
}

/** @type {import('../help-index.js').HelpScreen} */
export const BOD_ELA_HELP = {
  screenId: 'bod-ela',
  label: 'Elaboración',
  navPath: 'Bodega → Elaboración',
  keywords: ['elaboración', 'lote', 'fermentación', 'análisis', 'cata', 'estado', 'adición', 'insumo', 'herramienta', 'corte', 'muestra', 'sulfuroso', 'so2', 'alcohol potencial', 'metabisulfito'].map((k) => k.toLowerCase()),
  status: 'draft',
  summary: 'Seguimiento del vino: lotes, procesos, adiciones, análisis, herramientas y notas de cata.',
  sidebarItems: [
    {
      id: 'bod-ela-lots',
      label: 'Lotes de vino',
      description: 'Cada vino en bodega es un lote, aunque esté en varias vasijas.',
      detail: {
        sections: [
          {
            title: 'Qué ves',
            bullets: [
              'Fecha, código, vino, grupos, añada, estado, litros y vasijas donde está. La fecha es la de la cosecha, o la del corte si el lote nació de una mezcla. Si el corte junta vinos de distintas añadas, se ven todas con el porcentaje de litros. El filtro de añada incluye los lotes que tienen algo de ese año. También se puede buscar por número de lote. La lista está paginada.',
              '«Trazabilidad» abre la composición. El + de cada lote origen muestra de qué vasija vinieron esos litros. Se puede seguir hasta la cosecha. Si a esa pileta entraron varios CIU, aparecen todos. Si el lote ya no tiene litros, se ve igual el origen y, abajo, a dónde fue ese vino.',
              'El botón + crea un lote manual (por ejemplo una compra). Después lo ingresás a una vasija con un ajuste de entrada, en Vasijas → Movimientos.',
            ],
          },
        ],
      },
    },
    {
      id: 'bod-ela-proc',
      label: 'Procesos y estados',
      description: 'Fermentación, filtrado, crianza y el resto de los procesos.',
      detail: {
        sections: [
          {
            title: 'Cómo se usa',
            bullets: [
              'Elegí el lote y el proceso. Si el proceso tiene un estado resultante, el lote pasa a ese estado.',
              '«Cambio de estado» pide el estado nuevo y deja el cambio en el historial.',
              'La vasija es opcional, salvo en el descube y el desborre. Ahí elegís la vasija, los litros que quedan o los litros de borra, y si el líquido o la borra van a otra vasija.',
              'El descube pide el vino de cada vasija destino y pasa el lote a vino nuevo. Si los vinos son distintos, cada vasija queda con su lote. El desborre no cambia el estado: solo saca la borra.',
              'En un corte se puede elegir el vino del resultado. Si los lotes no traen el mismo vino, hay que elegirlo.',
              'Los procesos y los estados se mantienen en Maestros.',
            ],
          },
        ],
      },
    },
    {
      id: 'bod-ela-add',
      label: 'Adiciones',
      description: 'Ácido, bentonita, sulfuroso y el resto de lo que se agrega a una vasija.',
      detail: {
        sections: [
          {
            title: 'Cómo se carga',
            bullets: [
              'Elegí la vasija con vino, el insumo, la cantidad y la unidad (gramos, kilos, mililitros o litros).',
              'La dosis se calcula sola: gramos o mililitros por hectolitro si hay líquido, o por 100 kg si la vasija está en encube.',
              'Si el insumo está ligado a un artículo, elegí el depósito. El stock baja en la unidad de ese artículo: 300 gramos de un artículo en kilos descuentan 0,3 kg.',
              'Si el artículo no tiene unidad, o la unidad no es de peso ni de volumen, la adición no se guarda y hay que corregir el artículo.',
              'Anular la adición devuelve el stock.',
              'En la ficha de la vasija, la solapa Adiciones muestra las de ese vino. Si el vino se movió, también aparecen las de la vasija de origen, hasta la fecha del movimiento.',
            ],
          },
        ],
      },
    },
    {
      id: 'bod-ela-ana',
      label: 'Análisis',
      description: 'Alcohol, acidez, sulfuroso y el resto de los datos de laboratorio.',
      detail: {
        sections: [
          {
            title: 'Cómo se carga',
            bullets: [
              'Buscá la vasija, el lote o el fraccionamiento. Si el vino está en una vasija, la muestra queda de esa vasija. Si el lote ya no está en ninguna, se carga solo el lote. Un vino ya embotellado se busca por su número de lote de fraccionamiento.',
              'Elegí el origen: Laboratorio propio o INV. El del INV pide el número de análisis y se puede guardar sin valores; los valores se completan con Editar cuando llega el análisis.',
              'Completá solo los datos que midieron; el resto puede quedar vacío. Cada dato trae su unidad y su método, definidos en Maestros.',
              'Desde la ficha de la vasija también se carga un análisis, ya con esa vasija y ese lote. Esa muestra vale para esa vasija. No se copia a otra vasija del mismo lote, salvo que el vino se mueva hasta ahí.',
            ],
          },
          {
            title: 'Cómo encontrar uno',
            bullets: [
              'La lista muestra una tanda por vez. Abajo se ve cuántos hay, se puede pasar a la siguiente y elegir cuántos se ven juntos.',
              'Buscar mira el lote, el vino, la vasija, el laboratorio, las notas, el número de análisis INV y el número de lote del fraccionamiento.',
              'Origen deja solo los del laboratorio propio o solo los del INV.',
              'Desde y Hasta acotan la fecha de la muestra.',
              'Vasija y Lote dejan una sola vasija o un solo lote. Se pueden usar juntos. Quitar borra esa elección.',
            ],
          },
          {
            title: 'Cómo corregirlo',
            bullets: [
              'Editar abre la muestra: se puede cambiar la vasija o el lote, la fecha, el laboratorio, las notas y los valores. Un valor vacío se quita.',
              'Si el vino ya se movió de esa vasija, igual se pueden corregir la fecha y los valores. Para cambiar de vasija, esa vasija tiene que tener el lote ahora.',
              'Eliminar lo saca de la lista y de la ficha de la vasija.',
              'En la ficha de la vasija, Editar y Eliminar están en cada análisis anterior. El promedio y lo heredado no se editan: salen de las muestras.',
            ],
          },
        ],
      },
    },
    {
      id: 'bod-ela-tools',
      screenId: 'bod-ela-tools',
      label: 'Herramientas',
      description: 'Cuentas de corte, alcohol potencial y sulfuroso. No se guardan.',
      detail: {
        sections: [
          {
            title: 'Qué es',
            paragraphs: [
              'Son calculadoras. El resultado se ve en el momento y no queda guardado. No mueven vino ni cargan una adición.',
            ],
          },
          {
            title: 'Corte y muestra',
            bullets: [
              'Agregá una vasija o una fila a mano. La vasija trae los litros que tiene y los datos analíticos actuales, los mismos de la ficha. Podés cambiarlos.',
              'Los litros marcan la proporción. Podés poner 8000 y 2000, o 80 y 20.',
              'Si pedís más litros de los que hay en la vasija, se avisa y la cuenta sigue.',
              'Un dato entra al corte solo si todas las filas con litros lo tienen. Si a una le falta, se indica cuál y ese dato no se promedia.',
              'El promedio es por litros, igual que en la ficha después de un corte real. El pH y el sulfuroso molecular son una estimación.',
              'Si el corte tiene alcohol y azúcares, también se ve el alcohol potencial. Si tiene pH y sulfuroso libre, el sulfuroso molecular.',
              'La muestra, en mililitros, reparte esa proporción. Con 5000 ml y un corte de 8000 L y 2000 L, son 4000 ml y 1000 ml.',
            ],
          },
          {
            title: 'Alcohol potencial',
            fields: [
              { label: 'Azúcares', description: 'Gramos por litro. El alcohol de ese azúcar es los azúcares divididos el factor.' },
              { label: 'Alcohol actual', description: 'Opcional. Se suma al alcohol del azúcar.' },
              { label: 'Factor', description: 'Gramos de azúcar por cada 1% vol. Viene en 16,83 y se puede cambiar.' },
            ],
          },
          {
            title: 'Sulfuroso libre',
            bullets: [
              'Completá los litros, el sulfuroso libre actual y el deseado, en mg/L.',
              'La dosis es SO2 en gas, en gramos. Es directo: un gramo de gas es un gramo de sulfuroso.',
              'Por defecto se multiplica por 1,5 para compensar lo que se combina al agregarlo. Ese número se puede cambiar.',
              'Abajo quedan el metabisulfito de potasio y la solución, por si se usa otra forma. Salen de esos mismos gramos de gas. El rendimiento viene en 57,6% y la solución en 6%.',
              'Si el libre actual ya llega al deseado, o lo pasa, no propone agregar.',
            ],
          },
          {
            title: 'Sulfuroso molecular',
            bullets: [
              'Con el pH y el sulfuroso libre calcula el molecular a 20 °C. La constante que usa es 1,81 y se ve en la pantalla.',
              'Si indicás el molecular deseado, muestra el libre que haría falta.',
              'Si además ponés los litros, calcula la misma dosis que la pestaña de sulfuroso libre.',
              'Como referencia de estabilidad: cerca de 0,6 mg/L en tintos y 0,8 mg/L en blancos secos.',
            ],
          },
        ],
      },
    },
    {
      id: 'bod-ela-cata',
      label: 'Notas de cata',
      description: 'Cata del vino que está en vasija o ya fraccionado.',
      detail: {
        sections: [
          {
            title: 'Qué anotar',
            fields: [
              { label: 'Lote', description: 'El vino que se cata. Sirve también si ya no está en vasija.' },
              { label: 'Catador y puntaje', description: 'El puntaje va de 0 a 100 y es opcional.' },
              { label: 'Vista, nariz, boca y conclusión', description: 'Texto libre.' },
            ],
          },
        ],
      },
    },
  ],
}

/** @type {import('../help-index.js').HelpScreen} */
export const BOD_ORD_HELP = {
  screenId: 'bod-ord',
  label: 'Órdenes de trabajo',
  navPath: 'Bodega → Órdenes de trabajo',
  keywords: ['orden', 'ordenes', 'trabajo', 'tarea', 'cumplida', 'destinatario'].map((k) => k.toLowerCase()),
  status: 'draft',
  summary: 'Alguien arma la orden y elige a quién se la manda. Quien la recibe la marca cumplida y ahí se registra el movimiento.',
  sidebarItems: [
    {
      id: 'bod-ord-new',
      label: 'Armar una orden',
      description: 'Título, destinatarios y una o más tareas.',
      detail: {
        sections: [
          {
            title: 'Quién puede armarla',
            bullets: [
              'Hace falta el permiso de crear órdenes. Quien solo tiene permiso para verlas no ve este botón.',
              'El título es obligatorio. La nota es para el que va a hacer el trabajo.',
              'Elegí uno o más destinatarios. Cualquiera de ellos puede cumplir una tarea. No hace falta que la cumplan todos.',
              'Cada tarea es un movimiento, una adición, un fraccionamiento o un proceso. Se carga igual que en su pantalla, pero no se ejecuta todavía.',
              'Al guardar, cada destinatario recibe un aviso.',
              'Mientras la orden no esté cumplida, quien la armó puede editar el título, la nota, los destinatarios y agregar tareas. También puede sacar una tarea que sigue pendiente.',
              'Puede eliminar la orden si no está cumplida. Si alguna tarea ya se cumplió, eso queda registrado en la bodega y la orden deja de verse.',
            ],
          },
        ],
      },
    },
    {
      id: 'bod-ord-do',
      label: 'Cumplir la orden',
      description: 'Marcar cada tarea cumplida o no cumplida.',
      detail: {
        sections: [
          {
            title: 'Qué pasa al cumplirla',
            bullets: [
              'Cumplida registra esa operación con la fecha y los datos que puso quien armó la orden. El comentario es opcional.',
              'Si la vasija ya no tiene vino, o no hay stock, la tarea queda pendiente y se muestra el motivo.',
              'No cumplida no registra nada. Hay que escribir por qué.',
              'Si hay varias tareas, se pueden cumplir de a una. «Cumplir pendientes» las hace en orden y se detiene en la primera que no se pueda.',
              'La orden queda parcial mientras quede alguna pendiente, y cumplida cuando todas están cumplidas o no cumplidas.',
              'Quien solo tiene permiso de ver órdenes ve las suyas que todavía tienen algo pendiente. No ve el resto de la bodega.',
              'Anular solo se puede si todavía no se cumplió ninguna tarea. Eliminar la saca de la lista; anular la deja visible como anulada.',
              'La lista se parte en páginas. Se puede filtrar por cumplidas o no cumplidas (las parciales están con las no cumplidas) y por fechas.',
            ],
          },
        ],
      },
    },
  ],
}

/** @type {import('../help-index.js').HelpScreen} */
export const BOD_FRA_HELP = {
  screenId: 'bod-fra',
  label: 'Fraccionamiento',
  navPath: 'Bodega → Fraccionamiento',
  keywords: ['fraccionamiento', 'embotellado', 'botella', 'stock', 'insumos', 'corcho', 'etiqueta', 'inv', 'análisis'].map((k) => k.toLowerCase()),
  status: 'draft',
  summary: 'Saca litros de una vasija para embotellar. Si indicás artículo y depósito, las botellas entran al stock y se descuentan los insumos.',
  sidebarItems: [
    {
      id: 'bod-fra-new',
      label: 'Nuevo fraccionamiento',
      description: 'Litros, formato y botellas.',
      detail: {
        sections: [
          {
            title: 'Paso a paso',
            bullets: [
              'Elegí la vasija con vino y los litros que salen.',
              'El formato está en mililitros (por ejemplo 750). Las botellas se calculan solas y las podés corregir.',
              'El número de parte y el número de lote son opcionales. El lote de acá es el del embotellado, distinto del lote de vino de la vasija.',
              'Artículo y depósito son opcionales. Si los completás los dos, el stock de ese artículo suma las botellas.',
              'Si el lote queda sin litros en ninguna vasija, pasa a estado Fraccionado.',
              'Arriba se filtra por vino, número de lote y fechas. El número de lote también encuentra el lote de vino.',
              'El lápiz de la fila, o Editar en el detalle, corrige la fecha, el formato, las botellas, el número de parte, el número de lote, las notas y los insumos. La vasija, los litros, el artículo y el depósito no se cambian: para eso anulá el movimiento.',
              'Para deshacerlo, anulá el movimiento en Vasijas → Movimientos (si sigue siendo el último). También vuelven al stock los insumos. Si tiene análisis cargados, primero eliminalos.',
            ],
          },
        ],
      },
    },
    {
      id: 'bod-fra-supplies',
      label: 'Insumos',
      description: 'Botellas vacías, corchos, etiquetas y cajas que se descuentan del stock.',
      detail: {
        sections: [
          {
            title: 'Cómo funcionan',
            bullets: [
              'Cada artículo que se fracciona puede tener su matriz de insumos, en Maestros → Matriz de insumos: qué lleva cada botella y cuánto.',
              'Al elegir el artículo de stock, los insumos de la matriz aparecen solos, multiplicados por la cantidad de botellas.',
              'Podés cambiar la cantidad por botella o el total, elegir otro depósito o un lote, quitar los que no uses y agregar otros.',
              'Si cambiás las botellas, el total se recalcula, salvo en los insumos donde lo cambiaste a mano.',
              'Al registrar, cada insumo sale del stock con el movimiento «Insumos de fraccionamiento». Si no elegís lote, sale el más antiguo.',
              'En el detalle del fraccionamiento, «Insumos usados» muestra lo que se descontó y el número de lote de cada insumo. Si salió de más de un lote, se ve cada uno con su cantidad. Si después cambiás la matriz, esto no cambia.',
              'Desde Editar se pueden cambiar cantidades, depósito o lote, quitar un insumo o agregar otro. El stock se ajusta al guardar.',
            ],
          },
        ],
      },
    },
    {
      id: 'bod-fra-analyses',
      label: 'Análisis del vino fraccionado',
      description: 'Análisis del INV y del laboratorio propio.',
      detail: {
        sections: [
          {
            title: 'Cómo se ven y se cargan',
            bullets: [
              'El vino embotellado ya no está en una vasija, así que sus análisis se cargan en el fraccionamiento.',
              'Tocá una fila para ver el detalle. «Cargar análisis INV» pide el número de análisis del INV. Los valores se pueden completar después, con Editar.',
              '«Análisis de laboratorio» muestra los del laboratorio propio: los que cargues acá y los que se tomaron en la vasija antes de fraccionar (dicen «antes de fraccionar»).',
              '«Trazabilidad» también muestra los dos grupos de análisis y, con el +, la vasija de la que salió, con el vino que se embotelló ese día. No usa el vino que la vasija tenga ahora.',
              'En la lista, la columna INV muestra el número de análisis del INV.',
            ],
          },
        ],
      },
    },
  ],
}

/** @type {import('../help-index.js').HelpScreen} */
export const BOD_MAE_HELP = {
  screenId: 'bod-mae',
  label: 'Maestros',
  navPath: 'Bodega → Maestros',
  keywords: ['maestros', 'tipo de vasija', 'añada', 'vino', 'análisis', 'proceso', 'estado', 'finca', 'cuadro', 'insumo', 'matriz'].map((k) => k.toLowerCase()),
  status: 'draft',
  summary: 'Catálogos de la bodega. Varios vienen cargados y se pueden editar.',
  sidebarItems: [
    {
      id: 'bod-mae-items',
      label: 'Qué hay en el menú de la izquierda',
      description: 'Tipos de vasija, vinos, añadas, datos analíticos, estados y procesos.',
      detail: {
        sections: [
          {
            title: 'Cada opción',
            bullets: [
              'Tipos de vasija: tanque, pileta, barrica, etc. Marcá «Es barrica» para las que se usan unos años y después se dan de baja.',
              'Vinos: el nombre comercial del lote, cuando ya está elaborado.',
              'Categorías de vino: adentro hay Categorías y Grupos. El criterio lo define la bodega (calidad, destino u otro). Se asignan en el lote.',
              'Vinos: la variedad se elige de Variedades de uva.',
              'Fincas: adentro hay Fincas y Cuadros. La finca es propia o de terceros. El cuadro es una parcela de esa finca.',
              'Añadas: el año de cosecha. No es el ingreso de uva; eso se carga en Cosecha.',
              'Datos analíticos: alcohol, azúcares, acidez, sulfuroso, pH, densidad, extracto seco, cada uno con unidad y método.',
              'Estados del vino: mosto, fermentación, crianza, listo para fraccionar, etc.',
              'Insumos: lo que se agrega en elaboración. Se puede ligar a un artículo de stock. La unidad del artículo (kg, g, litro o ml) es la de la compra y la del descuento.',
              'Procesos: lo que se hace en bodega. Si tiene «estado resultante», al registrarlo el lote cambia de estado. «Cambio de estado» pide el estado a mano.',
              'Matriz de insumos: para cada artículo que se fracciona, los artículos que lleva cada botella (botella vacía, corcho, etiqueta, caja) y cuánto. Se usa al fraccionar.',
            ],
          },
        ],
      },
    },
  ],
}
